"use client";

import React from "react";
import Price from "@/components/common/Price";
import { HEADER_V2_DESIGN_SYSTEM } from "@/components/header/v2/designTokens";
import LABELS from "@/components/global-constant/src/general";
import { useCurrency } from "@/packages/context/CurrencyContext";

interface SideCartSummaryProps {
  subtotal: number;
  shippingFee: number;
  onCheckout: () => void;
  onViewCart: () => void;
  onClearCart: () => void;
}

export default function SideCartSummary({
  subtotal,
  shippingFee,
  onCheckout,
  onViewCart,
  onClearCart,
}: SideCartSummaryProps) {
  const { colors } = HEADER_V2_DESIGN_SYSTEM;
  const { exchangeRate } = useCurrency();
  const rate = exchangeRate && exchangeRate > 0 ? exchangeRate : 1;
  const baseShippingFee = shippingFee / rate;
  const total = subtotal + baseShippingFee;

  return (
    <div className="mt-4 pt-3 border-t border-slate-100 space-y-2 shrink-0">
      <div className="flex justify-between items-center text-xs text-slate-500 font-medium">
        <span>{LABELS.CART_SUMMARY?.SUBTOTAL || "Subtotal"}</span>
        <Price amount={subtotal} className="font-semibold text-slate-800" />
      </div>

      <div className="flex justify-between items-center text-xs text-slate-500 font-medium">
        <span>{LABELS.CART_SUMMARY?.SHIPPING || "Estimated Shipping"}</span>
        {shippingFee === 0 ? (
          <span className="font-bold text-emerald-600 text-xs uppercase tracking-wider">FREE</span>
        ) : (
          <Price amount={baseShippingFee} className="font-semibold text-slate-800" />
        )}
      </div>

      <div className="flex justify-between items-center text-sm font-bold text-slate-900 pt-1.5 border-t border-slate-100">
        <span>{LABELS.CART_SUMMARY?.TOTAL || "Total"}</span>
        <Price amount={total} className="text-base font-black text-slate-900" />
      </div>

      {/* Action Buttons */}
      <div className="space-y-2 pt-1">
        <button
          type="button"
          onClick={onCheckout}
          className={`w-full ${colors.primaryBg} hover:opacity-95 text-white text-xs font-bold uppercase tracking-wider py-3 rounded-xl shadow-md transition-all cursor-pointer`}
        >
          {LABELS.CART_SUMMARY?.CHECKOUT_BUTTON || "Proceed to Checkout"}
        </button>
        <button
          type="button"
          onClick={onViewCart}
          className={`w-full ${colors.accentBg} ${colors.accentBgHover} text-white text-xs font-bold uppercase tracking-wider py-3 rounded-xl shadow-md transition-all cursor-pointer`}
        >
          View Cart Page
        </button>
        <div className="text-center pt-1">
          <button
            type="button"
            onClick={onClearCart}
            className="text-[11px] font-semibold text-slate-400 hover:text-slate-600 underline cursor-pointer transition-colors"
          >
            Clear Cart
          </button>
        </div>
      </div>
    </div>
  );
}
